import React, {useCallback} from 'react';
import i18next from 'i18next';
import {StripeError} from '@stripe/stripe-js';
import {useElements, useStripe} from '@stripe/react-stripe-js';
import {safeCaptureException} from '../../../utils/sentry';

const StripeIncompleteCode = 'incomplete';

/**
 * Stripe not being ready is our bug, not something the guest did, and
 * "Elements instance is missing" was reaching them in English whatever
 * their language. Say something they can act on and keep the detail for us.
 */
function reportSetupFailure(detail: string) {
  safeCaptureException(new Error(`Unable to confirm payment. ${detail}`), 'stripe-setup');
  return i18next.t('try_again_or_contact');
}

function useStripePaymentElement(
  onError: (error: StripeError | string) => void,
  onCancel?: () => void,
) {
  const elements = useElements();
  const stripe = useStripe();
  const isConfirmingRef = React.useRef(false);

  const submitPaymentElement = useCallback(async () => {
    if (!elements) {
      onError(reportSetupFailure('Elements instance is missing.'));
      return false;
    }

    const {error: submitError} = await elements.submit();

    if (submitError) {
      console.error(submitError);
      if (submitError.code !== StripeIncompleteCode) {
        onError(submitError);
      } else {
        onCancel?.();
      }
      return false;
    }

    return true;
  }, [elements, onError, onCancel]);

  const confirmPaymentSetup = React.useCallback(
    async (clientSecret: string, billingDetails?: Record<string, unknown>) => {
      if (isConfirmingRef.current) {
        return;
      }

      if (!stripe) {
        onError(reportSetupFailure('Stripe instance is missing.'));
        return;
      }

      if (!elements) {
        onError(reportSetupFailure('Elements instance is missing.'));
        return;
      }

      isConfirmingRef.current = true;

      try {
        const response = await stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams: {
            payment_method_data: billingDetails
              ? {
                  billing_details: billingDetails,
                }
              : undefined,
            return_url: window.location.href,
          },
          redirect: 'if_required',
        });

        if (response.error) {
          onError(response.error);
        }

        return response;
      } finally {
        isConfirmingRef.current = false;
      }
    },
    [elements, onError, stripe],
  );

  return {
    submitPaymentElement,
    confirmPaymentSetup,
    isConfirmingRef,
  };
}

export {useStripePaymentElement};
